/**
 * @admin-portal/host-contract — v1
 *
 * The contract surface every module GUI consumes from the combined admin portal.
 * Types-first — minimal runtime (just the React context), so module agents import
 * from a single source of truth.
 *
 * See admin_portal/CONTRACT_v1.md for prose docs.
 */

import * as React from 'react';

/** The logged-in admin running the portal. Filled by web-svc via /auth/session. */
export interface HostUser {
  /** LINE user id, e.g. "Uxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" (33 chars). */
  lineUserId: string;
  /** LINE-issued display name. May be empty briefly after handshake. */
  displayName: string;
  /** LINE-issued CDN URL. May 410 after a while; host's avatar component handles fallback. */
  pictureUrl: string | null;
  /**
   * Role gate. Singleton literal in v1 because the edge function rejects
   * non-admins before they reach any module. Widen the union when operator/viewer roles ship.
   */
  role: 'admin';
}

/** Toast severity. */
export type ToastLevel = 'info' | 'warn' | 'error';

/** Confirmation modal options. */
export interface ConfirmOptions {
  title: string;
  body?: string;
  /** If true, the confirm button gets danger styling (red). For destructive actions. */
  danger?: boolean;
}

/**
 * The context every module GUI consumes via useHost().
 *
 * Modules MUST:
 *  - go through fetchApi for every backend call (never call Fly directly, never embed bearers)
 *  - use confirm() for destructive actions (revoke, refund, delete, mass-edit)
 *  - surface errors via toast(), not by redirecting on 401
 *
 * Modules MUST NOT:
 *  - read cookies, localStorage tokens, or any auth material
 *  - render their own LINE login UI, navbar, sidebar, html/body
 *  - mount global CSS rules (body{}, *, html, no resets)
 */
export interface HostCtx {
  /** Logged-in admin. */
  user: HostUser;

  /**
   * Fetch a backend path. Path is `/<module>/<rest>` (no /api prefix, no host).
   * Host prepends /api, adds credentials, validates session, injects bearer server-side.
   *
   * Example: hostCtx.fetchApi('/orders/sale-orders', { method: 'GET' })
   * Resolves to: GET /api/orders/sale-orders (browser) → ORDERS_BACKEND_URL/sale-orders (Fly)
   *
   * Idempotency-Key header is forwarded verbatim if you set it.
   */
  fetchApi: (path: string, init?: RequestInit) => Promise<Response>;

  /** End the session. Host clears the cookie + redirects to login. */
  signOut: () => void;

  /** Re-fetch the cached user from web-svc. Call after a mutation that affects the current admin's record. */
  refreshUser: () => Promise<void>;

  /** Show a toast. */
  toast: (level: ToastLevel, msg: string) => void;

  /** Show a confirm modal. Resolves true if user confirmed, false otherwise. */
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

/**
 * Read the host context. Throws if used outside <HostProvider>.
 * Modules use this everywhere.
 */
export const HostContext = React.createContext<HostCtx | null>(null);

export function useHost(): HostCtx {
  const ctx = React.useContext(HostContext);
  if (!ctx) {
    throw new Error(
      '[host-contract] useHost() called outside <HostProvider>. ' +
      'In standalone dev mode, wrap your app in <MockAuthProvider> from src/dev/.'
    );
  }
  return ctx;
}

/** The component shape every module GUI exports as its default. */
export interface ModuleAppProps {
  /**
   * Mount point in the host's URL tree, e.g. "/m/shopee".
   * Pass to react-router-dom's <BrowserRouter basename={basePath}> so module-internal links work.
   */
  basePath: string;
}

export type ModuleAppComponent = React.ComponentType<ModuleAppProps>;
